// Diet orders and the kitchen (docs/plans/phase3-kitchen-and-housekeeping.md §1): meal times,
// when a nil-by-mouth window covers a meal, and the checks on a diet order. Pure: callers pass
// the time. Diet types and textures are settings data (TODO(clinical-verify), plan K1 and K2);
// no clinical rule about them lives here.

import { err, ok, type Result } from '../result.ts';
import { parseIsoDay, startOfEatDay } from '../time.ts';

export const MEALS = ['breakfast', 'lunch', 'supper'] as const;
export type Meal = (typeof MEALS)[number];

/**
 * When each meal is served, East Africa Time (plan K3, provisional). A meal is excluded for a
 * patient whose nil-by-mouth window covers this instant.
 */
export const MEAL_TIMES: readonly { meal: Meal; label: string; at: string }[] = [
  { meal: 'breakfast', label: 'Breakfast', at: '07:00' },
  { meal: 'lunch', label: 'Lunch', at: '12:30' },
  { meal: 'supper', label: 'Supper', at: '18:00' },
];

export const MEAL_LABELS: Record<Meal, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  supper: 'Supper',
};

export const DIET_ORDER_STATUSES = ['active', 'superseded', 'entered_in_error'] as const;
export type DietOrderStatus = (typeof DIET_ORDER_STATUSES)[number];

export const DIET_ORDER_STATUS_LABELS: Record<DietOrderStatus, string> = {
  active: 'Current',
  superseded: 'Changed',
  entered_in_error: 'Entered in error',
};

export const KITCHEN_NOTE_MAX_LENGTH = 300;
/** Input limits for the fluid restriction, not a clinical threshold: the clinician sets it. */
export const FLUID_RESTRICTION_LIMITS = { min: 1, max: 9999 } as const;

/** The instant a meal is served on an East Africa day (`YYYY-MM-DD`). */
export function mealInstant(onDate: string, meal: Meal): Date {
  const day = parseIsoDay(onDate);
  if (!day.ok) throw new RangeError('not a day');
  const time = MEAL_TIMES.find((m) => m.meal === meal)?.at ?? '00:00';
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return new Date(startOfEatDay(day.value).getTime() + (hours * 60 + minutes) * 60_000);
}

export interface NilByMouthWindow {
  nilByMouth: boolean;
  nbmFrom: Date | null;
  /** Null: nil by mouth until the order is changed (plan K8). */
  nbmUntil: Date | null;
}

/** Whether the patient is nil by mouth at an instant: from the start, up to the end. */
export function nilByMouthAt(order: NilByMouthWindow, instant: Date): boolean {
  if (!order.nilByMouth || order.nbmFrom === null) return false;
  if (instant.getTime() < order.nbmFrom.getTime()) return false;
  return order.nbmUntil === null || instant.getTime() < order.nbmUntil.getTime();
}

export type DietOrderErrorCode =
  | 'diet.nbm_start_required'
  | 'diet.nbm_end_before_start'
  | 'diet.nbm_end_in_past'
  | 'diet.fluid_invalid'
  | 'diet.note_too_long';

export const DIET_ORDER_ERROR_MESSAGES: Record<DietOrderErrorCode, string> = {
  'diet.nbm_start_required': 'Say when nil by mouth starts.',
  'diet.nbm_end_before_start': 'Nil by mouth must end after it starts.',
  'diet.nbm_end_in_past':
    'That nil-by-mouth end has already passed. Leave it empty or choose a later time.',
  'diet.fluid_invalid': 'Write the fluid restriction in whole millilitres, from 1 to 9999.',
  'diet.note_too_long': `Keep the note for the kitchen to ${String(KITCHEN_NOTE_MAX_LENGTH)} characters.`,
};

export interface DietOrderInput {
  fluidRestrictionMl: number | null;
  nilByMouth: boolean;
  nbmFrom?: Date | null;
  nbmUntil?: Date | null;
  kitchenNote: string | null;
}

export interface CheckedDietOrder {
  fluidRestrictionMl: number | null;
  nilByMouth: boolean;
  nbmFrom: Date | null;
  nbmUntil: Date | null;
  kitchenNote: string | null;
}

/** The parts of a diet order Wodi checks; the diet and texture are checked against the lists. */
export function checkDietOrder(
  input: DietOrderInput,
  now: Date,
): Result<CheckedDietOrder, DietOrderErrorCode> {
  const ml = input.fluidRestrictionMl;
  if (
    ml !== null &&
    (!Number.isInteger(ml) ||
      ml < FLUID_RESTRICTION_LIMITS.min ||
      ml > FLUID_RESTRICTION_LIMITS.max)
  )
    return err('diet.fluid_invalid');
  const note = input.kitchenNote?.trim() ?? '';
  if (note.length > KITCHEN_NOTE_MAX_LENGTH) return err('diet.note_too_long');
  let nbmFrom: Date | null = null;
  let nbmUntil: Date | null = null;
  if (input.nilByMouth) {
    if (input.nbmFrom === undefined || input.nbmFrom === null)
      return err('diet.nbm_start_required');
    nbmFrom = input.nbmFrom;
    nbmUntil = input.nbmUntil ?? null;
    if (nbmUntil !== null) {
      if (nbmUntil.getTime() <= nbmFrom.getTime()) return err('diet.nbm_end_before_start');
      if (nbmUntil.getTime() <= now.getTime()) return err('diet.nbm_end_in_past');
    }
  }
  return ok({
    fluidRestrictionMl: ml,
    nilByMouth: input.nilByMouth,
    nbmFrom,
    nbmUntil,
    kitchenNote: note === '' ? null : note,
  });
}

/** "Diabetic, soft texture, fluids 1,500 mL a day". */
export function dietOrderWords(order: {
  dietDisplay: string;
  textureDisplay: string | null;
  fluidRestrictionMl: number | null;
}): string {
  const parts = [order.dietDisplay];
  if (order.textureDisplay !== null) parts.push(`${order.textureDisplay.toLowerCase()} texture`);
  if (order.fluidRestrictionMl !== null)
    parts.push(`fluids ${order.fluidRestrictionMl.toLocaleString('en-KE')} mL a day`);
  return parts.join(', ');
}
